import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  Globe, 
  FileText, 
  Image, 
  MessageSquare, 
  TrendingUp, 
  Eye,
  Edit,
  Plus,
  CheckCircle,
  AlertCircle,
  Clock,
  BarChart3,
  Users,
  ArrowUpRight,
  Zap,
  Calendar,
  Award,
  ExternalLink,
  Flag,
  Tag
} from 'lucide-react'
import { useQuery } from 'react-query'
import { apiService } from '../../services/api'
import { motion } from 'framer-motion'
import { useAuth } from '../../context/AuthContext'
import { useHasPermission } from '../../hooks/usePermissions'
import { Button } from '../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import LoadingSpinner from '../common/LoadingSpinner'
import OnboardingModal from './OnboardingModal'
import { useOnboarding, onboardingStorage } from '../../services/onboardingService'
import { cn } from '../../utils/cn'

// ── Site row helpers (mirrors ClientSites.jsx) ────────────────────────────
const SITE_GRADIENTS = [
  'from-violet-500 via-purple-500 to-indigo-600',
  'from-accent-500 via-accent-600 to-purple-700',
  'from-rose-500 via-pink-500 to-fuchsia-600',
  'from-emerald-500 via-teal-500 to-cyan-600',
  'from-orange-500 via-amber-500 to-yellow-500',
  'from-blue-500 via-indigo-500 to-violet-600',
  'from-pink-500 via-rose-500 to-red-500',
  'from-teal-500 via-cyan-500 to-blue-500',
]
const getSiteGradient = (name = '') => SITE_GRADIENTS[(name.charCodeAt(0) || 0) % SITE_GRADIENTS.length]
const getSiteEditorLogo = (site) => {
  try {
    const m = typeof site.meta_data === 'string' ? JSON.parse(site.meta_data) : (site.meta_data || {})
    return m.logo || null
  } catch { return null }
}
const getSiteTimeAgo = (ts) => {
  const d = Math.floor((Date.now() - new Date(ts)) / 86400000)
  if (d === 0) return 'Today'
  if (d === 1) return 'Yesterday'
  if (d < 7)  return `${d}d ago`
  return new Date(ts).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' })
}
const SITE_STATUS = {
  published: { color: 'bg-success-100 text-success-700 dark:bg-success-900/30 dark:text-success-300', label: 'Published' },
  draft:     { color: 'bg-warning-100 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300', label: 'Draft' },
}

// ── Request row helpers ───────────────────────────────────────────────────
const REQUEST_STATUS = {
  pending:     { color: 'text-warning-500 dark:text-warning-400', label: 'Pending',     border: 'border-l-warning-500',  icon: <Clock        className="w-4 h-4" /> },
  approved:    { color: 'text-success-500 dark:text-success-400', label: 'Approved',    border: 'border-l-success-500',  icon: <CheckCircle  className="w-4 h-4" /> },
  in_progress: { color: 'text-purple-500 dark:text-purple-400',   label: 'In Progress', border: 'border-l-purple-500',   icon: <Zap          className="w-4 h-4" /> },
  completed:   { color: 'text-accent-500 dark:text-accent-400',   label: 'Completed',   border: 'border-l-accent-500',   icon: <CheckCircle  className="w-4 h-4" /> },
  rejected:    { color: 'text-error-500 dark:text-error-400',     label: 'Rejected',    border: 'border-l-error-500',    icon: <AlertCircle  className="w-4 h-4" /> },
}
const REQUEST_PRIORITY = {
  urgent: { bg: 'bg-error-50 dark:bg-error-900/20',     icon: <AlertCircle  className="w-4 h-4 text-error-600 dark:text-error-400" /> },
  high:   { bg: 'bg-warning-50 dark:bg-warning-900/20', icon: <TrendingUp   className="w-4 h-4 text-warning-600 dark:text-warning-400" /> },
  medium: { bg: 'bg-accent-50 dark:bg-accent-900/20',   icon: <ArrowUpRight className="w-4 h-4 text-accent-600 dark:text-accent-400" /> },
  low:    { bg: 'bg-neutral-100 dark:bg-neutral-800',   icon: <Flag         className="w-4 h-4 text-neutral-400" /> },
}

const StatCard = ({ title, value, icon: Icon, color = 'primary', subtitle = null }) => (
  <Card className="hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-neutral-50 dark:from-dark-200 dark:to-dark-300 border border-neutral-200 dark:border-[#2f2b41] shadow-sm h-full">
    <CardContent className="p-6 h-full flex flex-col justify-between">
      <div className="flex items-center justify-between">
        <div className="space-y-2 flex-1">
          <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{title}</p>
          <p className={cn(
            'font-bold text-neutral-900 dark:text-neutral-50 leading-tight',
            typeof value === 'string' && value.length > 16 ? 'text-base' :
            typeof value === 'string' && value.length > 10 ? 'text-lg' :
            'text-2xl'
          )}>{value}</p>
          <p className="text-sm text-neutral-500 dark:text-neutral-400 min-h-[1.25rem]">{subtitle || ' '}</p>
        </div>
        <div className={cn(
          'h-12 w-12 rounded-xl flex items-center justify-center shadow-lg',
          color === 'primary' && 'bg-gradient-to-br from-accent-500 to-accent-600',
          color === 'green'   && 'bg-gradient-to-br from-success-500 to-success-600',
          color === 'blue'    && 'bg-gradient-to-br from-blue-500 to-blue-600',
          color === 'yellow'  && 'bg-gradient-to-br from-warning-500 to-warning-600'
        )}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
)

const QuickActions = ({ canEditText }) => (
  <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
    <CardHeader className="pb-4">
      <div className="flex items-center space-x-2">
        <Zap className="h-5 w-5 text-accent-600 dark:text-accent-400" />
        <CardTitle className="text-lg">Quick Actions</CardTitle>
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      <Button asChild className="w-full justify-start">
        <Link to="/client/sites/new">
          <Plus className="h-4 w-4 mr-2" />
          Create New Site
        </Link>
      </Button>
      {canEditText && (
        <Button variant="outline" asChild className="w-full justify-start">
          <Link to="/client/editor">
            <Edit className="h-4 w-4 mr-2" />
            Open Editor
          </Link>
        </Button>
      )}
      <Button variant="outline" asChild className="w-full justify-start">
        <Link to="/client/media">
          <Image className="h-4 w-4 mr-2" />
          Upload Media
        </Link>
      </Button>
      <Button variant="outline" asChild className="w-full justify-start">
        <Link to="/client/requests/new">
          <MessageSquare className="h-4 w-4 mr-2" />
          Request Changes
        </Link>
      </Button>
    </CardContent>
  </Card>
)

const ClientOverview = () => {
  const { user } = useAuth()
  const canEditText = useHasPermission('can_edit_text')
  const [selectedPeriod, setSelectedPeriod] = useState('30d')
  const [showOnboardingModal, setShowOnboardingModal] = useState(false)

  // Onboarding hook
  const {
    onboardingData,
    isLoading: onboardingLoading,
    shouldShowModal,
    isFirstLogin,
    getCompletionPercentage
  } = useOnboarding()

  // Fetch client profile and stats
  const { data: profile, isLoading: profileLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { staleTime: 10 * 60 * 1000 }
  )

  const { data: usage, isLoading: usageLoading } = useQuery(
    'client-usage',
    () => apiService.get('/clients/usage').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  const { data: sites, isLoading: sitesLoading, error: sitesError } = useQuery(
    'client-sites',
    () => apiService.get('/sites').then(res => res.data.sites || []),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  const { data: requests, isLoading: requestsLoading } = useQuery(
    'client-requests',
    () => apiService.get('/requests?limit=5').then(res => res.data),
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false
    }
  )

  // Mock data for demo
  const mockProfile = {
    company_name: 'Demo Company',
    subscription_plan: 'pro',
    edit_quota: 25,
    edits_used: 12,
    quota_reset_date: '2024-02-01',
    onboarding_completed: false,
    maintenance_mode: false
  }

  const mockSites = [
    { id: 1, name: 'Main Website', status: 'published', updated_at: '2024-01-15T10:30:00Z' },
    { id: 2, name: 'Landing Page', status: 'draft', updated_at: '2024-01-14T15:20:00Z' },
    { id: 3, name: 'Blog', status: 'published', updated_at: '2024-01-12T09:15:00Z' }
  ]

  const mockRequests = [
    { id: 1, title: 'Update homepage hero', status: 'pending', created_at: '2024-01-15T10:30:00Z' },
    { id: 2, title: 'Add new product page', status: 'approved', created_at: '2024-01-14T15:20:00Z' }
  ]

  const displayProfile = profile || mockProfile
  const displaySites = Array.isArray(sites) ? sites : (sites ? [] : mockSites)
  const displayRequests = requests || mockRequests

  // Auto-show onboarding modal on first login / when not yet completed
  useEffect(() => {
    if (onboardingLoading || !onboardingData) return
    if (
      user?.role === 'client' &&
      !onboardingData.onboardingCompleted &&
      !onboardingStorage.isSkippedForSession()
    ) {
      if (!onboardingData.firstLoginCompleted || !onboardingStorage.wasModalSeen()) {
        setShowOnboardingModal(true)
        onboardingStorage.markModalSeen()
      }
    }
  }, [user, onboardingData, onboardingLoading])

  const handleOnboardingComplete = () => {
    setShowOnboardingModal(false)
    onboardingStorage.setSkippedForSession(false) // Reset skip flag since it's completed
  }

  const handleOnboardingClose = () => {
    setShowOnboardingModal(false)
    onboardingStorage.setSkippedForSession(true)
  }

  if (profileLoading || sitesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    )
  }

  return (
    <>
      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboardingModal}
        onClose={handleOnboardingClose}
        onComplete={handleOnboardingComplete}
      />
      
      <div className="space-y-8 pt-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div className="space-y-3">
            <div>
              <h1 className="text-3xl font-bold text-neutral-900 dark:text-neutral-50">
                Welcome back, {user?.name}!
              </h1>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2">
                Here's what's happening with your websites.
              </p>
            </div>
            
            {/* Onboarding Progress Indicator */}
            {onboardingData && !onboardingData.onboardingCompleted && (
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2 px-3 py-2 bg-accent-50 dark:bg-accent-900/20 text-accent-700 dark:text-accent-300 rounded-lg border border-accent-200 dark:border-accent-800">
                  <div className="w-2 h-2 bg-accent-500 rounded-full animate-pulse"></div>
                  <span className="text-sm font-medium">
                    Setup {getCompletionPercentage()}% complete
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOnboardingModal(true)}
                >
                  Continue setup
                </Button>
              </div>
            )}
          </div>
        
          <div className="flex items-center space-x-3">
            {!!displayProfile.maintenance_mode && (
              <div className="flex items-center space-x-2 px-3 py-2 bg-warning-50 dark:bg-warning-900/20 text-warning-800 dark:text-warning-200 rounded-lg border border-warning-200 dark:border-warning-800">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Maintenance Mode</span>
              </div>
            )}
            
            <Button asChild>
              <Link to="/client/sites/new">
                <Plus className="h-4 w-4 mr-2" />
                New Site
              </Link>
            </Button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <StatCard
            title="My Sites"
            value={displaySites.length}
            icon={Globe}
            color="primary"
            subtitle={`${displaySites.filter(s => s.status === 'published').length} published`}
          />
          <StatCard
            title="Edit Quota"
            value={`${displayProfile.edits_used}/${displayProfile.edit_quota}`}
            icon={Edit}
            color="blue"
            subtitle="This month"
          />
          <StatCard
            title="Pending Requests"
            value={displayRequests.filter(r => r.status === 'pending').length}
            icon={MessageSquare}
            color="yellow"
            subtitle="Awaiting review"
          />
          <StatCard
            title="Plan"
            value={plansRaw?.[displayProfile.subscription_plan]?.name || displayProfile.subscription_plan?.toUpperCase()}
            icon={Award}
            color="green"
            subtitle="Current subscription"
          />
        </motion.div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Recent Sites */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-2"
          >
            <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Globe className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                    <CardTitle className="text-lg font-semibold">My Sites</CardTitle>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/client/sites">
                      View all
                      <ArrowUpRight className="h-4 w-4 ml-1" />
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {sitesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <LoadingSpinner />
                  </div>
                ) : displaySites.length > 0 ? (
                  <div className="space-y-2">
                    {displaySites.slice(0, 3).map((site, index) => {
                      const gradient = getSiteGradient(site.name)
                      const logoUrl  = getSiteEditorLogo(site)
                      const initial  = site.name?.charAt(0).toUpperCase() || '?'
                      const sc       = SITE_STATUS[site.status] || SITE_STATUS.draft
                      return (
                        <motion.div
                          key={site.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          className="flex items-center gap-4 p-3.5 rounded-xl border border-neutral-100 dark:border-[#2f2b41] bg-white dark:bg-[#252232] hover:border-accent-300 dark:hover:border-accent-600 hover:shadow-md transition-all"
                        >
                          {/* Logo / avatar */}
                          {logoUrl ? (
                            <div className="w-10 h-10 rounded-2xl border border-neutral-200 dark:border-[#2f2b41] bg-white dark:bg-[#221f2f] flex items-center justify-center overflow-hidden shrink-0 shadow-sm">
                              <img src={logoUrl} alt={site.name} className="w-full h-full object-contain p-1" />
                            </div>
                          ) : (
                            <div className={cn('w-10 h-10 rounded-2xl bg-gradient-to-br flex items-center justify-center shrink-0 shadow-sm', gradient)}>
                              <span className="text-sm font-bold text-white select-none">{initial}</span>
                            </div>
                          )}

                          {/* Name + meta */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                              {site.name}
                            </h4>
                            <div className="flex items-center gap-3 mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                              <span className="flex items-center gap-1 truncate">
                                <Globe className="h-3 w-3 shrink-0" />
                                {site.custom_domain || `/${site.slug}`}
                              </span>
                              <span className="flex items-center gap-1 shrink-0">
                                <Clock className="h-3 w-3" />
                                {getSiteTimeAgo(site.updated_at)}
                              </span>
                            </div>
                          </div>

                          {/* Status badge */}
                          <span className={cn('hidden sm:inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0', sc.color)}>
                            {sc.label}
                          </span>

                          {/* Actions */}
                          <div className="flex items-center gap-1 shrink-0">
                            {canEditText && (
                              <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0">
                                <Link to={`/client/editor?site=${site.id}`} title="Edit site">
                                  <Edit className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            )}
                            {site.status === 'published' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Visit live site"
                                onClick={() => window.open(site.custom_domain ? `https://${site.custom_domain}` : `${window.location.origin}/client-${site.client_id}`, '_blank', 'noopener,noreferrer')}
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0">
                                <Link to={`/client/preview/${site.id}`} title="Preview">
                                  <Eye className="h-3.5 w-3.5" />
                                </Link>
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      )
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 space-y-4">
                    <div className="mx-auto w-16 h-16 bg-accent-100 dark:bg-accent-900/20 rounded-full flex items-center justify-center">
                      <Globe className="h-8 w-8 text-accent-600 dark:text-accent-400" />
                    </div>
                    <div>
                      <p className="font-medium text-neutral-900 dark:text-neutral-100 mb-2">
                        No sites created yet
                      </p>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-4">
                        Get started by creating your first website
                      </p>
                      <Button asChild>
                        <Link to="/client/sites/new">
                          Create Your First Site
                        </Link>
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <QuickActions canEditText={canEditText} />
            </motion.div>
            
            {/* Recent Requests */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              <Card className="border-0 bg-gradient-to-br from-surface-50 to-surface-100 dark:from-dark-200 dark:to-dark-300">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MessageSquare className="h-5 w-5 text-accent-600 dark:text-accent-400" />
                      <CardTitle className="text-lg">Recent Requests</CardTitle>
                    </div>
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/client/requests">
                        View all
                        <ArrowUpRight className="h-4 w-4 ml-1" />
                      </Link>
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {requestsLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <LoadingSpinner />
                    </div>
                  ) : displayRequests.length > 0 ? (
                    <div className="space-y-2">
                      {displayRequests.slice(0, 3).map((request, index) => {
                        const sc  = REQUEST_STATUS[request.status]   || REQUEST_STATUS.pending
                        const pri = REQUEST_PRIORITY[request.priority] || REQUEST_PRIORITY.medium
                        return (
                          <motion.div
                            key={request.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className={cn(
                              'flex items-center gap-3 p-3.5 rounded-xl border border-neutral-100 dark:border-[#2f2b41] bg-white dark:bg-[#252232]',
                              'border-l-[3px] hover:shadow-md transition-all',
                              sc.border
                            )}
                          >
                            {/* Priority icon */}
                            <div className={cn('w-8 h-8 rounded-xl flex items-center justify-center shrink-0', pri.bg)}>
                              {pri.icon}
                            </div>

                            {/* Title + meta */}
                            <div className="flex-1 min-w-0">
                              {/* Title row: status icon + title */}
                              <div className="flex items-center gap-1.5 min-w-0">
                                {/* Status icon with custom tooltip */}
                                <div className="relative group shrink-0">
                                  <span className={cn('block', sc.color)}>{sc.icon}</span>
                                  <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-0.5 rounded text-xs bg-neutral-900 dark:bg-neutral-700 text-white whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 z-10">
                                    {sc.label}
                                  </span>
                                </div>
                                <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                                  {request.title}
                                </p>
                              </div>
                              {/* Meta row */}
                              <div className="flex items-center gap-2 mt-0.5 text-xs text-neutral-500 dark:text-neutral-400 min-w-0">
                                {request.site_name && (
                                  <span className="flex items-center gap-1 min-w-0 truncate">
                                    <Globe className="h-3 w-3 shrink-0" />
                                    <span className="truncate">{request.site_name}</span>
                                  </span>
                                )}
                                {request.category && (
                                  <span className="flex items-center gap-1 shrink-0 capitalize">
                                    <Tag className="h-3 w-3" />
                                    {request.category}
                                  </span>
                                )}
                                <span className="flex items-center gap-1 shrink-0">
                                  <Clock className="h-3 w-3" />
                                  {getSiteTimeAgo(request.created_at)}
                                </span>
                              </div>
                            </div>

                            {/* View button */}
                            <Button variant="ghost" size="sm" asChild className="h-8 w-8 p-0 shrink-0">
                              <Link to="/client/requests" title="View requests">
                                <ArrowUpRight className="h-3.5 w-3.5" />
                              </Link>
                            </Button>
                          </motion.div>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-6 space-y-3">
                      <div className="mx-auto w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-full flex items-center justify-center">
                        <MessageSquare className="h-6 w-6 text-neutral-400" />
                      </div>
                      <p className="text-sm text-neutral-500 dark:text-neutral-400">
                        No requests yet
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  )
}

export default ClientOverview